import { useState } from 'react'
import { PeriodHistoryList } from './PeriodHistoryList'
import type { BonusConfigWithDetails } from '@/hooks/use-bonuses'

interface BonusConfigCardProps {
  config: BonusConfigWithDetails
  onInstanceClick: (instanceId: string) => void
  onEdit: (config: BonusConfigWithDetails) => void
  onDelete: (configId: string) => void
  onBackfill: (configId: string) => void
  onArchive?: (configId: string) => void
  isBackfilling?: boolean
  isArchiving?: boolean
  isArchived?: boolean
}

export function BonusConfigCard({ config, onInstanceClick, onEdit, onDelete, onBackfill, onArchive, isBackfilling, isArchiving, isArchived }: BonusConfigCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const notMetCount = (config.instance_count || 0) - (config.met_count || 0)
  // All bonuses with periodic goals (weekly/monthly/etc) support backfill
  const hasPeriodicGoal = config.goal_timeframe && config.goal_timeframe !== 'one_time'

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm(`Delete bonus config for ${config.user_name}?`)) {
      onDelete(config.id)
    }
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit(config)
  }

  const handleBackfill = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Backfill will create instances for all past periods. Continue?')) {
      onBackfill(config.id)
    }
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Archive this bonus config? This is permanent and will stop future period evaluations.')) {
      onArchive?.(config.id)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header - always visible */}
      <div className="flex items-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-lg">
              {isExpanded ? '▼' : '▶'}
            </span>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">
                  {config.goal_metric_name || 'Unknown Goal'}
                </h3>
                {isArchived && (
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">
                    Archived
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {config.user_name} • {config.goal_timeframe}
                {hasPeriodicGoal ? ' • Recurring' : ''}
                {isArchived && config.archived_at && (
                  <> • Archived {new Date(config.archived_at * 1000).toLocaleDateString()}</>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Summary badges */}
            {(config.instance_count || 0) > 0 && (
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-0.5 bg-success-100 text-success-700 rounded-full">
                  Met: {config.met_count || 0}
                </span>
                {notMetCount > 0 && (
                  <span className="px-2 py-0.5 bg-danger-100 text-danger-700 rounded-full">
                    Not Met: {notMetCount}
                  </span>
                )}
              </div>
            )}

            <div className="text-right">
              <span className="text-lg font-semibold text-gray-900">
                {config.currency === 'USD' ? '$' : config.currency}
                {config.amount.toLocaleString()}
              </span>
            </div>
          </div>
        </button>

        {/* Action buttons */}
        <div className="flex items-center gap-1 pr-4">
          {!isArchived && hasPeriodicGoal && (
            <button
              onClick={handleBackfill}
              disabled={isBackfilling}
              className="p-2 text-gray-400 hover:text-brand-teal hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
              title="Backfill past periods"
            >
              {isBackfilling ? '⏳' : '↻'}
            </button>
          )}
          {!isArchived && (
            <button
              onClick={handleEdit}
              className="p-2 text-gray-400 hover:text-brand-teal hover:bg-gray-100 rounded transition-colors"
              title="Edit bonus config"
            >
              ✎
            </button>
          )}
          {!isArchived && onArchive && (
            <button
              onClick={handleArchive}
              disabled={isArchiving}
              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
              title="Archive bonus config"
            >
              {isArchiving ? '⏳' : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              )}
            </button>
          )}
          {!isArchived && (
            <button
              onClick={handleDelete}
              className="p-2 text-gray-400 hover:text-danger-600 hover:bg-gray-100 rounded transition-colors"
              title="Delete bonus config"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Expanded content - lazy loaded */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          <PeriodHistoryList
            configId={config.id}
            onInstanceClick={onInstanceClick}
          />
        </div>
      )}
    </div>
  )
}
